import React, { useState } from "react";
import useTeamMembersAndTeams from "../utils/useTeamMembersAndTeams";
import { CATEGORY_LABEL } from "../utils/widgetConstants";

// SG-2164: shown in place of the preview panel when an ADMIN clicks "+ Add widget" on a
// graph-capable widget (see widgetHasGraph in utils/widgetConstants.js). Non-admins and
// KPI-only/no-chart widgets always skip this and add directly — WidgetCatalog.js's handleAdd
// only opens this panel when both conditions hold, so this component can assume isAdmin === true.
// Interaction modeled on the design mock's ConfigurePanel (Z_taskCode/SG-2164-fe-code), scoped down
// to what's actually backend-wired today: Audience Scope. (Visualization Type is not a persisted
// per-widget setting in the real backend, so it's not offered here.)
// SG-2164 (req change): "Use the dashboard's current member/team filter" is now a separate checkbox
// above the radio group (see useGlobal below). "Everyone" is now a real widget-level scope = every
// team member, irrespective of the global filter — so it no longer carries the old sub-text.
const AUDIENCE_OPTIONS = [
  { id: "EVERYONE", label: "Everyone", sub: null },
  { id: "SPECIFIC_USERS", label: "Members", sub: null },
  { id: "SPECIFIC_TEAMS", label: "Teams", sub: null }
];

const ConfigureWidgetPanel = ({ widget, onBack, onConfirm, isAdding }) => {
  // Checked (default) = follow the global dashboard filter, persisted as audienceScope=null.
  // Unchecked = use the widget-level scope chosen in the radio group below.
  const [useGlobal, setUseGlobal] = useState(true);
  const [mode, setMode] = useState("EVERYONE");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const { members, teams, loading } = useTeamMembersAndTeams();

  const toggleUser = id =>
    setSelectedUsers(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleTeam = id =>
    setSelectedTeams(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const handleConfirm = () => {
    if (useGlobal) {
      onConfirm(null);
      return;
    }
    const audienceScope =
      mode === "EVERYONE"
        ? { type: "EVERYONE", memberIds: [], teamIds: [] }
        : mode === "SPECIFIC_USERS"
        ? { type: "SPECIFIC_USERS", memberIds: selectedUsers, teamIds: [] }
        : { type: "SPECIFIC_TEAMS", memberIds: [], teamIds: selectedTeams };
    onConfirm(audienceScope);
  };

  return (
    <div className="configureWidgetPanel">
      <button type="button" className="configureWidgetBackBtn" onClick={onBack}>
        ← Back to widgets
      </button>

      <p className="configureWidgetLabel">Configure</p>
      <div className="configureWidgetTitleRow">
        <p className="configureWidgetTitle">{widget.name}</p>
        <span className="widgetCatalogCatBadge">{CATEGORY_LABEL[widget.category] || widget.category}</span>
      </div>

      <div className="configureWidgetSection">
        <div
          className={`audienceScopeGlobalRow${useGlobal ? " audienceScopeGlobalRowActive" : ""}`}
          onClick={() => setUseGlobal(v => !v)}
        >
          <span className={`audienceScopeCheckbox${useGlobal ? " audienceScopeCheckboxChecked" : ""}`} />
          <p className="audienceScopeGlobalLabel">Use the dashboard's current member/team filter</p>
        </div>

        <div className={`audienceScopeScopeBlock${useGlobal ? " audienceScopeScopeBlockDisabled" : ""}`}>
        <p className="configureWidgetSectionTitle">Show data for</p>
        <div className="audienceScopeOptions">
          {AUDIENCE_OPTIONS.map(opt => (
            <React.Fragment key={opt.id}>
              <div
                className={`audienceScopeOption${mode === opt.id ? " audienceScopeOptionActive" : ""}`}
                onClick={() => !useGlobal && setMode(opt.id)}
              >
                <span className="audienceScopeOptionRadio" />
                <div>
                  <p className="audienceScopeOptionLabel">{opt.label}</p>
                  {opt.sub && <p className="audienceScopeOptionSub">{opt.sub}</p>}
                </div>
              </div>

              {/* SG-2164: list renders directly under its own option, not at the bottom of the panel. */}
              {opt.id === "SPECIFIC_USERS" && mode === "SPECIFIC_USERS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading members…</p>}
                  {!loading &&
                    members.map(m => {
                      const checked = selectedUsers.includes(m.memberId);
                      return (
                        <div key={m.memberId} className="audienceScopeListRow" onClick={() => toggleUser(m.memberId)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{m.name}</span>
                        </div>
                      );
                    })}
                  {!loading && !members.length && <p className="audienceScopeEmpty">No members found</p>}
                </div>
              )}

              {opt.id === "SPECIFIC_TEAMS" && mode === "SPECIFIC_TEAMS" && (
                <div className="audienceScopeList">
                  {loading && <p className="audienceScopeLoading">Loading teams…</p>}
                  {!loading &&
                    teams.map(t => {
                      const checked = selectedTeams.includes(t.id);
                      return (
                        <div key={t.id} className="audienceScopeListRow" onClick={() => toggleTeam(t.id)}>
                          <span className={`audienceScopeCheckbox${checked ? " audienceScopeCheckboxChecked" : ""}`} />
                          <span className="audienceScopeListLabel">{t.name}</span>
                        </div>
                      );
                    })}
                  {!loading && !teams.length && <p className="audienceScopeEmpty">No teams found</p>}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        </div>
      </div>

      <button
        type="button"
        className="configureWidgetConfirmBtn"
        onClick={handleConfirm}
        disabled={isAdding}
      >
        {isAdding ? "Adding…" : "Add to dashboard"}
      </button>
    </div>
  );
};

export default ConfigureWidgetPanel;
